<template>
  <div>
    <Head title="Inventory Transfers" />
    <h1 class="mb-8 text-3xl font-bold">Inventory Transfers</h1>
    <div class="bg-white rounded-md shadow overflow-hidden mb-5">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-2 -mr-6 pt-8 pl-8">
          <div class="pb-2 pr-6 mb-3 w-full lg:w-1/3">
            <Checkbox label="Cushions" :active="form.is_sku_transfer" @input="form.is_sku_transfer = $event"/>
          </div>
        </div>
        <div class="flex flex-wrap -mb-2 -mr-6 pt-8 pl-8 pr-8 pb-8">
          <div class="w-1/2 lg:w-1/2">
            <select-input
              v-model="form.location"
              :error="form.errors.location"
              class="pb-2 pr-6 w-full lg:w-full"
              @change="updateScans"
            >
              <option :value="null">
                Select the location
              </option>
              <option
                v-for="location in locations"
                :key="location.id"
                :value="location.id"
              >
                {{ location.name }}
              </option>
            </select-input>
          </div>
          <div v-show="form.is_sku_transfer" class="w-1/2 lg:w-1/4">
            <text-input
              v-model="form.quantity"
              :error="form.errors.quantity"
              class="pb-2 pr-6 w-full lg:w-full"
              placeholder="Quantity"
            />
          </div>
          <div v-if="form.is_sku_transfer" class="w-1/2 lg:w-1/4">
            <text-input
              v-model="form.item_id"
              :error="form.errors.item_id"
              class="pb-2 pr-6 w-full lg:w-full"
              placeholder="Scan Part Number"
            />
          </div>
          <div v-else class="w-1/2 lg:w-1/2">
            <text-input
              v-model="form.item_id"
              :error="form.errors.item_id"
              class="pb-2 pr-6 w-full lg:w-full"
              placeholder="Scan UID"
            />
          </div>
        </div>
        <div
          class="flex items-center justify-end w-full px-8 py-4 bg-gray-50 border-t border-gray-100"
        >
          <loading-button
            :loading="form.processing"
            class="btn-orange px-3 py-2 text-xs"
            type="submit"
          >
            Transfer
          </loading-button>
        </div>
      </form>
    </div>
    <hr />
    <ul
      id="tabs-tabFill"
      class="nav nav-tabs flex flex-col md:flex-row flex-wrap list-none border-b-0 pl-0 mb-4"
      role="tablist"
    >
      <li class="nav-item flex-auto text-center" role="presentation">
        <a
          id="tabs-home-tabFill"
          href="#tabs-homeFill"
          class="
          nav-link
          w-full
          block
          font-medium
          text-xs
          leading-tight
          uppercase
          border-x-0 border-t-0 border-b-2 border-transparent
          px-6
          py-3
          my-2
          hover:border-transparent hover:bg-gray-100
          focus:border-transparent
          active" 
          data-bs-toggle="pill" 
          data-bs-target="#tabs-homeFill" 
          role="tab"
          aria-controls="tabs-homeFill"
          aria-selected="true"
        >
          Today's Scans
        </a>
      </li>
      <li
        class="nav-item flex-auto text-center"
        role="presentation"
      >
        <a 
          id="tabs-profile-tabFill"
          href="#tabs-profileFill"
          class="
          nav-link
          w-full
          block
          font-medium
          text-xs
          leading-tight
          uppercase
          border-x-0 border-t-0 border-b-2 border-transparent
          px-6
          py-3
          my-2
          hover:border-transparent hover:bg-gray-100
          focus:border-transparent"
          data-bs-toggle="pill"
          data-bs-target="#tabs-profileFill"
          role="tab"
          aria-controls="tabs-profileFill"
          aria-selected="false"
        >
          All Scans
        </a>
      </li>
    </ul>
    <div id="tabs-tabContentFill" class="tab-content">
      <div id="tabs-homeFill" class="tab-pane fade show active" role="tabpanel" aria-labelledby="tabs-home-tabFill">
        <div class="bg-white rounded-md shadow overflow-x-auto">
          <table class="w-full whitespace-nowrap">
            <tr class="text-left font-bold">
              <th class="pb-3 pt-3 px-3">Date</th>
              <th class="pb-3 pt-3 px-3">Item UID</th>
              <th class="pb-3 pt-3 px-3">Part Number</th>
              <th class="pb-3 pt-3 px-3">Description</th>
              <th class="pb-3 pt-3 px-3">Quantity</th>
              <th class="pb-3 pt-3 px-3">Source</th>
              <th class="pb-3 pt-3 px-3">Destination</th>
              <th class="pb-3 pt-3 px-3">UoM</th>
              <th class="pb-3 pt-3 px-3">Status</th>
              <th class="pb-3 pt-3 px-3" colspan="2">User</th>
            </tr>
            <tr v-for="item in transfers.data" :key="item.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 focus:text-orange-500">
                  {{ item.date }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.item_uid }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.part_no }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.description }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.quantity }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.location }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.destination }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.measure }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.status }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.user }}
                </Link>
              </td>
              <td class="w-px border-t">
                <Link class="flex items-center px-3" tabindex="-1">
                  <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
                </Link>
              </td>
            </tr>
            <tr v-if="transfers.data.length === 0">
              <td class="px-3 py-3 border-t" colspan="4">No data found</td>
            </tr>
          </table>
        </div>
        <pagination class="mt-6" :links="transfers.links" />
      </div>
      <div id="tabs-profileFill" class="tab-pane fade" role="tabpanel" aria-labelledby="tabs-profile-tabFill">
        <div class="flex items-center justify-between mb-6">
          <date-range v-model="search.dateValue" class="mr-0 w-full max-w-md mr-3" @reset="reset" />
          <a
            v-if="allTransfers.data.length > 0"
            class="btn-orange px-3 py-2 text-xs cursor-pointer"
            :href="`/location/transfer/download/${search.dateValue}`"
            download
            target="__blank"
          >
            <span>Download</span>
          </a>
        </div>
        <div class="bg-white rounded-md shadow overflow-x-auto">
          <table class="w-full whitespace-nowrap">
            <tr class="text-left font-bold">
              <th class="pb-3 pt-3 px-3">Date</th>
              <th class="pb-3 pt-3 px-3">Item UID</th>
              <th class="pb-3 pt-3 px-3">Part Number</th>
              <th class="pb-3 pt-3 px-3">Description</th>
              <th class="pb-3 pt-3 px-3">Quantity</th>
              <th class="pb-3 pt-3 px-3">Source</th>
              <th class="pb-3 pt-3 px-3">Destination</th>
              <th class="pb-3 pt-3 px-3">UoM</th>
              <th class="pb-3 pt-3 px-3">Status</th>
              <th class="pb-3 pt-3 px-3" colspan="2">User</th>
            </tr>
            <tr v-for="item in allTransfers.data" :key="item.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 focus:text-orange-500">
                  {{ item.date }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.item_uid }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.part_no }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.description }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.quantity }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.location }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.destination }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.measure }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.status }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.user }}
                </Link>
              </td>
              <td class="w-px border-t">
                <Link class="flex items-center px-3" tabindex="-1">
                  <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
                </Link>
              </td>
            </tr>
            <tr v-if="allTransfers.data.length === 0">
              <td class="px-3 py-3 border-t" colspan="4">No data found</td>
            </tr>
          </table>
        </div>
        <pagination class="mt-6" :links="allTransfers.links" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import DateRange from '@/Shared/DateRange'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import Pagination from '@/Shared/Pagination'
import Checkbox from '@/Shared/Checkbox'

export default {
  components: {
    Head,
    Link,
    DateRange,
    TextInput,
    SelectInput,
    LoadingButton,
    Pagination,
    Checkbox,
  },
  layout: Layout,
  props: {
    filters: Object,
    locations: Object,
    transfers: Object,
    allTransfers: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        item_id: null,
        location: null,
        quantity: null,
        is_sku_transfer: false,
      }),
      search: {
        dateValue: this.filters.dateValue,
      },
      summary: {
        dateRange: this.filters.dateRange,
      },
    }
  },
  computed: {
    role() {
      return this.$inertia.page.props.auth.user.role
    },
  },
  watch: {
    search: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/location-transfers/', pickBy(this.search), { preserveState: true })
      }, 150),
    },
    summary: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/location-transfers/', pickBy(this.summary), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    store() {
      this.form.post('/location-transfers', {
        onSuccess: () => {
          this.form.reset(
            'item_id',
            'quantity',
          )
          this.form.is_sku_transfer = false
        },
      })
    },
  },
}
</script>
